import { Component, input, output, forwardRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ComponentSize } from '../types';

@Component({
  selector: 'app-search',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchComponent),
      multi: true
    }
  ],
  template: `
    <div class="relative">
      <div class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
      <input
        type="text"
        [id]="id() || name()"
        [name]="name()"
        [placeholder]="placeholder()"
        [disabled]="disabled()"
        [readonly]="readonly()"
        [class]="inputClasses"
        [value]="value()"
        (input)="onInput($event)"
        (blur)="onBlur()"
        (focus)="onFocus()"
        (keydown.enter)="onSearch()" />
      @if (value() && clearable()) {
        <button
          type="button"
          (click)="clear()"
          class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      }
    </div>
  `,
  styles: []
})
export class SearchComponent implements ControlValueAccessor {
  placeholder = input<string>('Search...');
  disabled = input<boolean>(false);
  readonly = input<boolean>(false);
  clearable = input<boolean>(true);
  size = input<ComponentSize>('md');
  id = input<string>('');
  name = input<string>('');
  class = input<string>('');

  searchEvent = output<string>();

  value = signal<string>('');
  touched = signal(false);

  private onChange = (value: string) => {};
  private onTouched = () => {};

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const newValue = target.value;
    this.value.set(newValue);
    this.onChange(newValue);
    this.markAsTouched();
  }

  onSearch(): void {
    this.searchEvent.emit(this.value());
  }

  clear(): void {
    this.value.set('');
    this.onChange('');
  }

  onBlur(): void {
    this.markAsTouched();
    this.onTouched();
  }

  onFocus(): void {
    // Focus handling
  }

  private markAsTouched(): void {
    if (!this.touched()) {
      this.touched.set(true);
    }
  }

  writeValue(value: string): void {
    this.value.set(value || '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handled by input signal
  }

  get inputClasses(): string {
    const classes = [
      'w-full',
      'pl-10',
      'pr-10',
      'border',
      'rounded-lg',
      'transition-all',
      'duration-200',
      'focus:outline-none',
      'placeholder:text-gray-400',
      'dark:placeholder:text-gray-500'
    ];

    const sizeMap = {
      xs: 'px-2 py-1 text-xs pl-8 pr-8',
      sm: 'px-3 py-2 text-sm pl-9 pr-9',
      md: 'px-4 py-3 text-base pl-10 pr-10',
      lg: 'px-5 py-4 text-lg pl-12 pr-12',
      xl: 'px-6 py-5 text-xl pl-14 pr-14'
    };
    classes.push(sizeMap[this.size()]);

    classes.push('border-gray-300', 'dark:border-gray-600', 'focus:border-blue-600', 'dark:focus:border-blue-500', 'focus:ring-2', 'focus:ring-blue-100', 'dark:focus:ring-blue-900/30');

    if (this.disabled()) {
      classes.push('opacity-60', 'cursor-not-allowed', 'bg-gray-50', 'dark:bg-gray-800');
    } else {
      classes.push('bg-white', 'dark:bg-gray-800', 'text-gray-900', 'dark:text-gray-100');
    }

    const customClass = this.class();
    if (customClass) {
      classes.push(customClass);
    }

    return classes.join(' ');
  }
}

